import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSiteContext } from "../../context/SiteContext";
import userService from "../../services/userServices";
import {
  LayoutDashboard,
  User,
  Users,
  Palette,
  LayoutTemplate,
  Video,
  Calendar,
  Film,
  Mail,
  Bot,
  Plug,
  CreditCard,
  Settings,
  HelpCircle,
  Crown,
  Layers,
  MessageSquare,
  Tags,
  Presentation,
  Share2,
  Globe,
  FileSignature,
} from "lucide-react";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { user } = useSiteContext();
  const [isDesktop, setIsDesktop] = useState(false);
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(false);

  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);

    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  useEffect(() => {
    userService.getUserProfile()
      .then((res) => {
        const profile = res?.user || res?.data?.user || res?.data;
        if (profile && profile.whitelabel_enabled) {
          setWhitelabelEnabled(true);
        }
      })
      .catch(() => { /* ignore */ });
  }, []);

  const isTeamMember = user?.isTeamMember === true;
  const isMemberRole = user?.role === "member";
  const isBusinessPlan = user?.plan === "Business";
  const isHostPlan = user?.plan === "host" || user?.plan === "co-host";
  // TODO: remove isAdmin gate once Event Landing Pages launches publicly
  const canAccessEventLandingPages = user?.plan !== "Basic" && user?.isAdmin === true;

  const memberHiddenItems = new Set([
    "Team / Users",
    "Brand Kits",
    "Room Templates",
    "Signed Contracts",
    "Integrations",
    "White Label",
    "Billing & Usage",
    "Settings",
  ]);

  // Items hidden for host/co-host plan users (non-subscriber team members
  // invited by a Business-plan account). They access shared elements and
  // assigned rooms only — no admin / billing / Webinar / AI controls.
  const hostHiddenItems = new Set([
    "Team / Users",
    "Room Templates",
    "Webinars",
    "Leads",
    "AI Notetaker",
    "Integrations",
    "White Label",
    "Billing & Usage",
    "Affiliate Program",
    "Event Landing Pages",
    "Signed Contracts",
  ]);

  const memberMenuGroups = [
    {
      items: [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
        { name: "Account", href: "/dashboard/account", icon: User },
        { name: "Team / Users", href: null, icon: Users, comingSoon: true },
      ],
    },
    {
      items: [
        { name: "Elements", href: "/dashboard/elements", icon: Layers },
        { name: "Brand Kits", href: "/dashboard/brand-kits", icon: Palette, premium: true },
        { name: "Room Templates", href: null, icon: LayoutTemplate, comingSoon: true },
      ],
    },
    {
      items: [
        { name: "Rooms", href: "/dashboard/rooms", icon: Video },
        { name: "Schedule", href: "/dashboard/schedule", icon: Calendar },
        { name: "Recordings", href: "/dashboard/recordings", icon: Film },
        { name: "Webinars", href: "/dashboard/webinars", icon: Presentation, premium: true },
        ...(canAccessEventLandingPages ? [{ name: "Event Landing Pages", href: "/dashboard/event-landing-pages", icon: Globe }] : []),
        { name: "Signed Contracts", href: "/dashboard/signed-contracts", icon: FileSignature },
      ],
    },
    {
      items: [
        { name: "Profile", href: "/dashboard/profile", icon: User },
        { name: "Billing", href: "/dashboard/billing", icon: CreditCard },
        { name: "Support", href: "/dashboard/help-support", icon: HelpCircle },
        // { name: "Support", href: "/dashboard/support", icon: HelpCircle },
      ],
    },
  ];

  // Menu structure matching the design
  const fullMenuGroups = [
    {
      items: [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
        { name: "Account", href: "/dashboard/account", icon: User },
        // Team / Users is in soft-launch — restricted to internal admins only
        // (User.isAdmin === true). Lift to isBusinessPlan when rolling out
        // to all Business subscribers.
        ...(user?.isAdmin
          ? [{ name: "Team / Users", href: "/dashboard/team-users", icon: Users }]
          : []),
      ],
    },
    {
      items: [
        { name: "Elements", href: "/dashboard/elements", icon: Layers },
        { name: "Brand Kits", href: "/dashboard/brand-kits", icon: Palette, premium: true },
        { name: "Room Templates", href: null, icon: LayoutTemplate, comingSoon: true },
      ],
    },
    {
      items: [
        { name: "Rooms", href: "/dashboard/rooms", icon: Video },
        { name: "Schedule", href: "/dashboard/schedule", icon: Calendar },
        { name: "Recordings", href: "/dashboard/recordings", icon: Film },
        { name: "Webinars", href: "/dashboard/webinars", icon: Presentation, premium: true },
        ...(canAccessEventLandingPages ? [{ name: "Event Landing Pages", href: "/dashboard/event-landing-pages", icon: Globe }] : []),
        { name: "Signed Contracts", href: "/dashboard/signed-contracts", icon: FileSignature },
        // Polls menu hidden (not removed)
        // ...(user?.plan === "Business"
        //   ? [{ name: "Polls", href: "/dashboard/polls", icon: MessageSquare }]
        //   : []),
      ],
    },
    {
      items: [
        { name: "Leads", href: "/dashboard/leads", icon: Mail },
        // { name: "Feedback", href: "/dashboard/feedback", icon: MessageSquare },
        { name: "AI Notetaker", href: "/dashboard/ai-notetaker-settings", icon: Bot, premium: true },
        { name: "Integrations", href: "/dashboard/integrations", icon: Plug },
        { name: "White Label", href: "/dashboard/white-label", icon: Tags, premium: true },
      ],
    },
    {
      items: [
        ...(user?.isAdmin
          ? [{ name: "Affiliate Program", href: "/dashboard/affiliate", icon: Share2 }]
          : []),
        { name: "Billing & Usage", href: "/dashboard/billing", icon: CreditCard },
        { name: "Settings", href: null, icon: Settings, comingSoon: true },
        { name: "Support", href: "/dashboard/help-support", icon: HelpCircle },
        // { name: "Support", href: "/dashboard/support", icon: HelpCircle },
      ],
    },
  ];

  const applyMemberFilter = (groups) =>
    groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !memberHiddenItems.has(item.name)),
      }))
      .filter((group) => group.items.length > 0);

  const applyHostFilter = (groups) =>
    groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !hostHiddenItems.has(item.name)),
      }))
      .filter((group) => group.items.length > 0);

  const baseMenuGroups = isTeamMember ? memberMenuGroups : fullMenuGroups;
  let menuGroups = isMemberRole ? applyMemberFilter(baseMenuGroups) : baseMenuGroups;
  if (isHostPlan) menuGroups = applyHostFilter(menuGroups);
  const filteredMenuGroups = menuGroups;

  const isActiveRoute = (item) => {
    if (!item.href) return false;
    if (item.href === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    if (item.name === "Elements") {
      return (
        location.pathname === "/dashboard/elements" ||
        location.pathname.includes("/folders") ||
        location.pathname.includes("/elemets")
      );
    }
    return location.pathname === item.href || location.pathname.startsWith(item.href + "/");
  };

  return (
    <div
      className={`fixed left-0 top-14 h-[calc(100%-3.5rem)] flex flex-col transition-all duration-300 z-30 transform ${
        isOpen
          ? "translate-x-0 w-64"
          : "-translate-x-full w-64 md:translate-x-0 md:w-16"
      }`}
      style={{
        backgroundColor: isOpen
          ? isDesktop
            ? "rgba(0, 18, 51, 0.95)"
            : "var(--bk-background, #001233)"
          : "rgba(0, 18, 51, 0.95)",
      }}
    >
      {/* Header Section */}
      <div className={`flex-shrink-0 p-4 border-b border-white/10 ${!isOpen && isDesktop ? "hidden" : ""}`}>
        <h2 className="text-lg font-bold text-white font-sans">{isTeamMember ? "Member Portal" : "Admin Panel"}</h2>
        <div className="flex items-center gap-2 mt-2">
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              isBusinessPlan
                ? "bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] text-white"
                : isHostPlan
                ? "bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[#3b82f6] text-white"
                : "bg-[#556177] text-gray-200"
            }`}
          >
            {isBusinessPlan
              ? "Business Plan"
              : user?.plan === "host"
              ? "Host"
              : user?.plan === "co-host"
              ? "Co-Host"
              : "Basic Plan"}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent ${!isOpen && isDesktop ? "px-2 py-4" : "p-4"}`}>
        <div className="space-y-1">
          {menuGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {group.items.map((item) => {
                const isActive = isActiveRoute(item);
                const Icon = item.icon;

                if (item.name === "White Label" && whitelabelEnabled) {
                  const handleWhitelabelClick = (e) => {
                    e.preventDefault();
                    const token = localStorage.getItem("token") || "";
                    const url = token
                      ? `https://whitelabel.r-link.com/Sso?token=${encodeURIComponent(token)}`
                      : "https://whitelabel.r-link.com/AdminLogin";
                    window.open(url, "_blank", "noopener,noreferrer");
                    if (!isDesktop && toggleSidebar) {
                      toggleSidebar();
                    }
                  };
                  return (
                    <a
                      key={item.name}
                      href="https://whitelabel.r-link.com"
                      onClick={handleWhitelabelClick}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer ${
                        !isOpen && isDesktop ? "justify-center" : ""
                      }`}
                      title={!isOpen && isDesktop ? item.name : undefined}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {(isOpen || !isDesktop) && (
                        <span className="flex-1 text-sm font-medium font-sans">
                          {item.name}
                        </span>
                      )}
                    </a>
                  );
                }

                if (item.comingSoon || !item.href || (item.premium && !isBusinessPlan)) {
                  // Coming soon or premium-gated item (disabled)
                  return (
                    <div
                      key={item.name}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 cursor-not-allowed opacity-50 ${
                        !isOpen && isDesktop ? "justify-center" : ""
                      }`}
                      title={!isOpen && isDesktop ? item.name : (item.premium && !isBusinessPlan ? "Business Plan required" : "Coming Soon")}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {(isOpen || !isDesktop) && (
                        <>
                          <span className="flex-1 text-sm font-medium font-sans">
                            {item.name}
                          </span>
                        </>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      if (item.href === "/dashboard") {
                        window.dispatchEvent(new Event("dashboard-clicked"));
                      }
                      if (!isDesktop && toggleSidebar) {
                        toggleSidebar();
                      }
                    }}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                      !isOpen && isDesktop ? "justify-center" : ""
                    } ${
                      isActive
                        ? "bg-[var(--bk-primary,#6a1fbf)] text-white"
                        : "text-gray-300 hover:text-white hover:bg-white/5"
                    }`}
                    title={!isOpen && isDesktop ? item.name : undefined}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {(isOpen || !isDesktop) && (
                      <span className="flex-1 text-sm font-medium font-sans">
                        {item.name}
                      </span>
                    )}
                  </NavLink>
                );
              })}
              {groupIndex < menuGroups.length - 1 && (
                <div className="my-3 border-t border-white/10" />
              )}
            </div>
          ))}
        </div>
      </nav>

      {/* Upgrade CTA - Only show for non-business users */}
      {!isBusinessPlan && !isTeamMember && isOpen && (
        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <div className="bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)]/20 to-[var(--bk-accent,#00c853)]/20 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-[var(--bk-accent,#00c853)]" />
              <span className="text-sm font-semibold text-white font-sans">
                Upgrade to Business
              </span>
            </div>
            <p className="text-xs text-gray-400 mb-3 font-sans">
              Unlock team management, advanced branding, templates & more.
            </p>
            <a
              href="https://go.r-link.com/account"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity font-sans text-center"
            >
              Upgrade Now
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
